/**
 * Projects a TYPED create action — `create_automation`, `create_validation_rule`
 * — onto the CRM properties its approval will write.
 *
 * Generic CRM proposals arrive as timeline events that are already
 * property-keyed (`ahoy_object_created`, `ahoy_property_updated`). These two
 * are different: the agent fills a flat typed payload with its own field names
 * (`display_name`, `condition`, `source`, …) and the backend handler maps it to
 * `ahoy_*` properties at execute time. Anything that wants to show the record
 * BEFORE approval has to perform the same mapping — and this module is the
 * only place it lives, so the builder panel, the action-center record
 * slideout, and every other preview consumer agree on what the draft looks
 * like.
 *
 * The mapping mirrors the backend exactly, minus what the server owns:
 * - `apps/automations/actions/create_automation/handler.py`
 *   (`_build_properties` / `_build_scheduled_properties`): display name,
 *   summary, documentation, the script (`{source, fixtures}`), the config
 *   pair (`ahoy_config` + the server-stamped `ahoy_config_schema`, projected
 *   together exactly as the backend writes them together), and EITHER the
 *   schedule (⇒ `ahoy_scheduled_automation`) OR the triggering type lists
 *   (⇒ `ahoy_event_automation`; stamped from the script's `event` annotation
 *   at proposal creation). Deliberately omitted: `ahoy_enabled` (forced
 *   to "false" server-side; an unsaved draft reads "Draft" regardless) and
 *   `ahoy_automation_name` / `ahoy_permissions` (server-minted, `internal`
 *   read access — they never render on the saved record either).
 * - `apps/crm_events/actions/create_validation_rule/handler.py`
 *   (`build_rule_events`): every key, one-to-one.
 */

import type {
  CreateAutomationPayload,
  CreateValidationRulePayload,
  ProposedAction,
} from '@/lib/proposals/core/types';
import {
  isCreateAutomationPayload,
  isCreateValidationRulePayload,
} from '@/lib/proposals/core/types';
import {
  AUTOMATION_ENTITY_TYPE,
  SCHEDULED_AUTOMATION_ENTITY_TYPE,
  VALIDATION_RULE_ENTITY_TYPE,
} from '@/lib/reasoning/entityTypes';

export interface TypedCreateProjection {
  /** The id minted in the handler's `transform()` — the id the entity gets at approval. */
  entityId: string;
  /** The CONCRETE type approval creates, e.g. scheduled vs event automation. */
  entityTypeName: string;
  /** Plain (unwrapped) values keyed by CRM property name. */
  properties: Record<string, unknown>;
}

function ruleProperties(
  payload: CreateValidationRulePayload
): Record<string, unknown> {
  return {
    ahoy_display_name: payload.display_name,
    ahoy_target_entity_type: payload.target_entity_type_name,
    ahoy_condition: payload.condition,
    ahoy_constraint: payload.constraint,
    ahoy_message: payload.message,
    // The schema declares this a boolean on rules but a "true"/"false" select
    // on automations, and every existing read site compares against the
    // string — as does the backend, which writes the string form.
    ahoy_enabled: String(payload.enabled),
  };
}

/**
 * The concrete automation type a `create_automation` payload will create.
 *
 * Mirrors the backend discriminator exactly (`_is_scheduled` in
 * apps/automations/actions/create_automation/handler.py): a payload carrying
 * a `schedule` becomes an `ahoy_scheduled_automation`; every other one becomes
 * an `ahoy_event_automation`.
 */
function automationEntityType(payload: CreateAutomationPayload): string {
  return payload.schedule
    ? SCHEDULED_AUTOMATION_ENTITY_TYPE
    : AUTOMATION_ENTITY_TYPE;
}

function automationProperties(
  payload: CreateAutomationPayload
): Record<string, unknown> {
  const properties: Record<string, unknown> = {
    ahoy_display_name: payload.display_name,
    ahoy_summary: payload.summary,
    ahoy_documentation: payload.documentation,
  };
  // Only the keys the concrete type owns, under the same property names the
  // backend writes, so the projection never carries a foreign key and the
  // type-specific sections (triggers, schedule) read the draft exactly as
  // they read the saved record.
  if (payload.schedule) {
    properties.ahoy_schedule = payload.schedule;
  } else {
    properties.ahoy_triggering_entity_types = payload.triggering_entity_types;
    properties.ahoy_triggering_event_types = payload.triggering_event_types;
  }
  // The script travels as ONE property in the same `{source, fixtures}` shape
  // the backend stores, so the Code and Test Cases tabs read a draft through
  // the exact path they read a saved record — no side channel.
  const hasSource =
    typeof payload.source === 'string' && payload.source.trim().length > 0;
  const fixtures = Array.isArray(payload.fixtures) ? payload.fixtures : [];
  if (hasSource || fixtures.length > 0) {
    properties.ahoy_script = {
      source: hasSource ? payload.source : '',
      fixtures,
    };
  }
  // Config travels as the same pair approval writes — the values plus the
  // server-stamped schema they decompose through — and only as a pair,
  // mirroring `_build_properties`: the record surface's Settings section
  // gates on the schema and lays the values out with it, so one without the
  // other renders nothing anyway.
  const configSchema = payload.config_schema;
  if (
    configSchema &&
    Object.keys(configSchema).length > 0 &&
    payload.config != null
  ) {
    properties.ahoy_config_schema = configSchema;
    properties.ahoy_config = payload.config;
  }
  return properties;
}

/**
 * The projection for a typed create action, or `undefined` for every other
 * action type. Keyed on `proposed_action_type_name` — the server's own
 * discriminator — rather than payload shape, because the payload guards are
 * lenient (`isCreateAutomationPayload` accepts anything with a
 * `display_name`) and a rule payload satisfies the automation guard too.
 */
export function projectTypedCreatePayload(
  action: ProposedAction
): TypedCreateProjection | undefined {
  const { payload } = action;
  switch (action.proposed_action_type_name) {
    case 'create_validation_rule':
      if (!isCreateValidationRulePayload(payload)) {
        return undefined;
      }
      return {
        entityId: payload.entity_id,
        entityTypeName: VALIDATION_RULE_ENTITY_TYPE,
        properties: ruleProperties(payload),
      };
    case 'create_automation':
      if (!isCreateAutomationPayload(payload)) {
        return undefined;
      }
      return {
        entityId: payload.entity_id,
        entityTypeName: automationEntityType(payload),
        properties: automationProperties(payload),
      };
    default:
      return undefined;
  }
}

/** True for the action types `projectTypedCreatePayload` understands. */
export function isTypedCreateAction(action: ProposedAction): boolean {
  return (
    action.proposed_action_type_name === 'create_automation' ||
    action.proposed_action_type_name === 'create_validation_rule'
  );
}
